<template>
  <div class="summary">
    <div 
      class="close-btn"
      @click.stop="$emit('update:selected-position', null)">
      X
    </div>

    <div class="summary-title title">
      <span>{{ position.positionName }}</span>
    </div>

    <div class="selected-role-indicator">
      <div>{{ position.selectedRole.prettyName }}</div>
      <DutyLabel :duty="position.selectedRole.duty" />
    </div>

    <div class="section-title flex">
      <span>{{ $t('tactics.instructions.instructions') }}</span>
      <button 
        v-if="position.positionNumber > 1"
        class="instruction__edit-button"
        @click="showPIModal = true">
          {{ $t('ui.edit') }}
      </button>
    </div>

    <div>
      <div 
        v-for="instruction in positionPlayerInstructions"
        :key="instruction.name"
        :class="['instruction', {'instruction--selected': !instruction.partOfRole}]">
        {{ $t(`tactics.pis.${instruction.name}.${instruction.value > 0 ? "more": "less"}`) }}
      </div>
    </div>
    <div>
      <div class="instruction-key instruction-key--selected"></div>
      <div class="instruction-key__label ">{{ $t('tactics.instructions.playerInstructions.selected') }}</div>
      <div class="instruction-key"></div>
      <div class="instruction-key__label">{{ $t('tactics.instructions.playerInstructions.partOfRole') }}</div>
    </div>

    <div class="detail-selectors">
      <span 
        :class="[
          'detail-selector-btn', 
          {'detail-selector-btn--selected': selectedSummary === 'forwardMovement'}
        ]"
        @click.stop="selectedSummary = 'forwardMovement'">
        {{ $t('ui.forwardMovementScores') }}
      </span>
      <span 
        :class="[
          'detail-selector-btn', 
          {'detail-selector-btn--selected': selectedSummary === 'responsibilities'}
        ]"
        @click.stop="selectedSummary = 'responsibilities'">
        {{ $t('tactics.responsibilities.positionalResponsibilities') }}
      </span>
      <span 
        :class="[
          'detail-selector-btn', 
          {'detail-selector-btn--selected': selectedSummary === 'partnerships'}
        ]"
        @click.stop="selectedSummary = 'partnerships'">
        {{ $t('ui.partnerships') }}
      </span>
    </div>

    <div class="role-analyser-link">
      For a more detailed analysis, visit <a href="https://www.guidetofm.com/tactics/roles-duties/#role-analyser" target="_blank">https://www.guidetofm.com/tactics/roles-duties/#role-analyser</a>.
    </div>

    <div class="section" v-if="selectedSummary === 'forwardMovement'">
      <div>
        <span>{{ capitalise($t('stats.forwardMovement.penetrationScore')) }}: </span>
        <span class="forward-movement-score">{{ positionData.penetrationScore }}</span>
      </div>
      <div>
        <span>{{ capitalise($t('stats.forwardMovement.solidityScore')) }}: </span>
        <span class="forward-movement-score">{{ positionData.solidityScore }}</span>
      </div>
      <div>
        <span>{{ capitalise($t('stats.forwardMovement.supportScore')) }}: </span>
        <span class="forward-movement-score">{{ positionData.supportScore }}</span>
      </div>
    </div>

    <div class="section" v-if="selectedSummary === 'responsibilities'">
      <div 
        class="pitch-area-performance-summary"
        v-for="pitchArea in pitchAreas" 
        :key="pitchArea">
        <div class="title">
          {{ $t(`tactics.pitchAreas.${pitchArea}`) }}
        </div>
        <div 
          v-for="(responsibility, key) in position[pitchArea].responsibilityPerformance"
          :key="key"
          :class="['responsibility', { 'responsibility--not-performed': !responsibility.performs && !responsibility.contributes }]">
          <span>{{ capitalise($t(`tactics.responsibilities.${key}`)) }}</span>
          <span class="performance-indicator">{{ showPerformance(responsibility) }}</span>
        </div>
      </div>
    </div>

    <div class="section" v-if="selectedSummary === 'partnerships'">
      <div 
        class="pitch-area-performance-summary"
        v-for="pitchArea in pitchAreas" 
        :key="pitchArea">
        <div class="title">
          {{ $t(`tactics.pitchAreas.${pitchArea}`) }}
        </div>

        <div v-if="!position[pitchArea].partnerability" class="hint">
          {{ $t('ui.noPartnerships') }}
        </div>
        <div 
          v-else
          v-for="(partner, partnership) in position[pitchArea].partnerability"
          :key="partnership"
          :class="['partner', { 'partner--not-partner': !partner }]">
          <span>{{ capitalise($t(`tactics.partnerships.${partnershipTranslationKey(partnership)}`)) }}</span>
          <span 
            v-if="partner !== false" 
            class="partner-type">
            {{ partner.type.toLowerCase() + " Partner" }}
          </span>
        </div>
      </div>
    </div>

    <PIModal 
      v-if="showPIModal"
      :position="position"
      @update:show-pi-modal="showPIModal = $event" />

  </div>
</template>

<script>
import DutyLabel from "@/components/DutyLabel";
import PIModal from "@/components/ThePlayerInstructionModal";

export default {
  components: {
    DutyLabel,
    PIModal,
  },

  props: {
    position: Object
  },

  data: function() {    
    return {
      selectedSummary: "forwardMovement",
      showPIModal: false,
    };
  },

  computed: {
    positionData() {
      const positions = this.$store.getters.positions();
      return positions.filter(pos => pos.positionNumber === this.position.positionNumber)[0];
    },

    positionPlayerInstructions() {
      return this.$store.getters.positionPlayerInstructions(this.position.positionNumber)
          .sort((a, b) => {
            if (a.partOfRole) return 1;
            if (b.partOfRole) return -1;
            return 0;
          });
    },

    pitchAreas() {
      const pitchAreas = [];

      if (this.position.positionGroup === "DC" || this.position.positionGroup === "DMC") pitchAreas.push('centralDefence');
      if (this.position.side !== 'CENTRE' && this.position.positionGroup !== 'STC' && this.position.positionGroup !== 'GK') pitchAreas.push('flanks');
      if (this.position.stratum.height === 2 || this.position.stratum.height === 3 || this.position.positionGroup === "DL/DR" || this.position.positionGroup === "DC") pitchAreas.push('centralMidfield');
      if (this.position.stratum.height >= 4 || this.position.positionGroup === 'MC') pitchAreas.push('centralAttack');

      return pitchAreas;
    }
  },

  methods: {
    capitalise(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    showPerformance(responsibility) {
      if (!responsibility.performs) return "-";
      if (!responsibility.atypically) {
        return this.$t(`tactics.responsibilities.${responsibility.performs}.full`);
      }
      return `${this.$t(`tactics.responsibilities.${responsibility.performs}.full`)} ${this.$t('tactics.responsibilities.atypically.full')}`;
    },

    partnershipTranslationKey(partnershipKey) {
      if (partnershipKey === 'interchanging') return 'balancedInterchanging';
      return partnershipKey;
    }
  }
}
</script>

<style scoped>
.summary {
  position: fixed;
  background: #004C3F;
  color: #fff;
  padding: 32px 24px 8px 16px;
  font-size: small;
  text-align: left;
  top: 0;
  right: 0;
  height: 100vh;
  width: max-content;
  max-width: calc(100vw - 52px);
  z-index: 100;
}

.close-btn {
  position: fixed;
  top: 12px;
  right: 12px;
  background: #449E45;
  padding: 8px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
}

.summary-title {
  font-size: medium;
}

.summary-title > span {
  margin-right: 6px;
}

.selected-role-indicator {
  margin: 12px 0;
  background: #006766;
  padding: 6px 12px;
  border-radius: 4px;
}

.role-analyser-link {
  font-size: x-small;
}
.role-analyser-link a {
  color: #fff;
}

.section {
  margin-top: 12px;
}

.section-title {
  text-transform: uppercase;
  font-weight: bold;
  margin: 32px 0 8px;

}

.title {
  font-weight: bold;
  padding: 2px;
  text-transform: capitalize;
}

.flex {
  display: flex;
  align-items: center;
}

.instruction__edit-button {
  background-color: #22604E;
  color: #fff;
  border: none;
  text-transform: capitalize;
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  margin-left: auto;
  display: none;
}

.instruction__edit-button:hover {
  background-color: #306A59;
}

.instruction {
  color: #00952B;
  padding: 4px;
}

.instruction:nth-of-type(odd) {
  background: #045044;
}

.instruction--selected {
  color: #11DD5E;
}

.instruction-key__label {
  display: inline;
  margin-right: 16px;
  text-transform: capitalize;
}

.instruction-key {
  border-radius: 50%;
  background-color: #00952B;
  width: 16px;
  height: 16px;
  display: inline-block;
  margin-right: 6px;
  margin-top: 12px;
}

.instruction-key--selected {
  background-color: #11DD5E;
}

.responsibility, .partner {
  margin-left: 8px;
}

.responsibility--not-performed, .partner--not-partner {
  opacity: 0.55;
}

.pitch-area-performance-summary {
  margin: 4px 0 6px;
}

.performance-indicator, .partner-type, .forward-movement-score {
  margin-left: 8px;
  text-transform: capitalize;
  opacity: 0.75;
}

.performance-indicator, .partner-type {
  font-style: italic;
}

.detail-selectors {
  margin: 20px 0;
  display: flex;
}

.detail-selector-btn {
  padding: 8px 12px;
  text-transform: capitalize;
  cursor: pointer;
  font-size: xx-small
}

.detail-selector-btn--selected {
  border-bottom: solid 3px white;
}

.hint {
  font-style: italic;
  font-size: small;
  opacity: 0.87;
}

@media only screen and (min-width: 1340px) {
  .summary {
    display: block;
  }
}

@media only screen and (min-width: 740px) {
  .instruction__edit-button {
    display: initial;
  }

  .detail-selector-btn {
    font-size: small;
  }
}
</style>
